/**
 * فضای کاری فاکتور — مشاهده/ویرایش پیوسته با قبلی/بعدی روی لیست فیلترشده
 * Route: /supply-chain/events/:eventId
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Button, Space, Tag, Form, Input, InputNumber, Select, message, Descriptions, Typography, Dropdown, Tabs, Spin, Collapse, Checkbox, Table } from "antd";
import Alert from "../../components/GxAlert";
import {
  LeftOutlined, RightOutlined, SaveOutlined, EditOutlined, CloseOutlined,
  CheckOutlined, StopOutlined, RollbackOutlined, DeleteOutlined,
  MoreOutlined, CommentOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  scGetEvent, scUpdateEvent, scApproveEvent, scCancelEvent, scRestoreEvent, scDeleteEvent,
  scGetParties, scGetItems, scEventHistory,
} from "../../api/client";
import { JalaliDateInput, toJalali, toJalaliDateTime } from "../../utils/dateUtils";
import { useAuth } from "../../context/AuthContext";
import { eventLabel, eventColor, statusMeta, fmtKg, fmtRial } from "./scConstants";
import { getTemplate } from "./movementTemplates";
import { loadInvoiceListCtx, saveInvoiceListCtx } from "../../utils/invoiceListCtx";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { useNavigationGuard } from "../../hooks/useNavigationGuard";
import HelpPopover from "../../components/HelpPopover";
import useColorOptions from "../../hooks/useColorOptions";
import { CreatableSelect } from "../../components/EditableTable";
import PartyPicker from "./PartyPicker";

const { Text, Title } = Typography;

function canEditRow(r, can) {
  if (!r) return false;
  if (r.status === "cancelled") return false;
  if (r.status === "confirmed") return can("sc.event.edit_approved");
  return can("sc.event.create");
}

export default function InvoiceWorkspace() {
  const { eventId } = useParams();
  const id = Number(eventId);
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const { tryNavigate } = useNavigationGuard();

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const loadHistory = async () => {
    setHistLoading(true);
    try { const r = await scEventHistory(id); setHistory(r.data); }
    catch { message.error("خطا در بارگذاری تاریخچه"); }
    setHistLoading(false);
  };
  useEffect(() => { setHistory(null); }, [id]);   // فاکتورِ دیگر → تاریخچهٔ خودش

  const [form] = Form.useForm();
  const { colorOptions, registerColor } = useColorOptions(
    useMemo(() => (row?.color ? [row.color] : []), [row]));
  const snapshotRef = useRef({});
  const [ctx, setCtx] = useState(() => {
    if (location.state?.ids) {
      const c = {
        ids: location.state.ids,
        index: location.state.index ?? 0,
        returnTo: location.state.returnTo || "/supply-chain/events",
        filters: location.state.filters || null,
      };
      saveInvoiceListCtx(c);
      return c;
    }
    return loadInvoiceListCtx();
  });

  const ids = ctx?.ids || [];
  const idx = ids.length ? ids.findIndex((x) => Number(x) === id) : -1;
  const total = ids.length || (row ? 1 : 0);
  const displayIdx = idx >= 0 ? idx : 0;

  const loadEvent = useCallback(async (eid) => {
    setLoading(true);
    try {
      const r = await scGetEvent(eid);
      setRow(r.data);
      form.resetFields();
      form.setFieldsValue({ ...r.data });
      snapshotRef.current = { ...(r.data || {}) };
      setEditing(false);
    } catch (e) {
      message.error(e?.response?.data?.detail || "فاکتور یافت نشد");
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    scGetParties().then((r) => setParties(r.data)).catch(() => {});
    scGetItems().then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (id) loadEvent(id);
  }, [id, loadEvent]);

  // همگام‌سازی index در context
  useEffect(() => {
    if (idx >= 0 && ctx) {
      const next = { ...ctx, index: idx };
      saveInvoiceListCtx(next);
      setCtx(next);
    }
    // eslint-disable-next-line
  }, [id]);

  const goClose = () => {
    const to = ctx?.returnTo || "/supply-chain/events";
    tryNavigate(to, (dest) => navigate(dest));
  };

  const goToId = (nextId) => {
    tryNavigate(`/supply-chain/events/${nextId}`, (dest) => navigate(dest, {
      state: { ids, index: ids.indexOf(nextId), returnTo: ctx?.returnTo, filters: ctx?.filters },
      replace: false,
    }));
  };

  const goPrev = () => {
    if (idx <= 0) return;
    goToId(ids[idx - 1]);
  };
  const goNext = () => {
    if (idx < 0 || idx >= ids.length - 1) return;
    goToId(ids[idx + 1]);
  };

  const doSave = useCallback(async () => {
    if (!row) return false;
    let v;
    try { v = await form.validateFields(); } catch { return false; }
    setSaving(true);
    try {
      await scUpdateEvent(row.id, v);
      message.success("ذخیره شد");
      const r = await scGetEvent(row.id);
      setRow(r.data);
      form.setFieldsValue({ ...r.data });
      snapshotRef.current = { ...(r.data || {}) };
      return true;
    } catch (e) {
      message.error(e?.response?.data?.detail || "خطا در ذخیره");
      return false;
    } finally {
      setSaving(false);
    }
  }, [row, form]);

  const saveAndNext = async () => {
    if (await doSave()) {
      if (idx >= 0 && idx < ids.length - 1) goToId(ids[idx + 1]);
    }
  };

  const canFinalize = useCallback(() => {
    if (!editing) return { ok: false, reason: "در حالت مشاهده هستید" };
    if (!canEditRow(row, can)) return { ok: false, reason: "مجوز ویرایش ندارید" };
    return { ok: true, reason: "" };
  }, [editing, row, can]);

  useUnsavedChangesGuard({
    enabled: editing,
    getValues: () => form.getFieldsValue(true),
    snapshotRef,
    canFinalize,
    onSaveDraft: doSave,
    onFinalize: doSave,
    onDiscard: () => {
      if (row) {
        form.resetFields();
        form.setFieldsValue({ ...row });
        snapshotRef.current = { ...row };
      }
      setEditing(false);
    },
  });

  useEffect(() => {
    if (!editing) return undefined;
    const h = (e) => {
      if (!e.altKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); goNext(); }
      if (e.key === "ArrowRight") { e.preventDefault(); goPrev(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const startEdit = () => {
    if (!canEditRow(row, can)) {
      message.warning("مجوز ویرایش این فاکتور را ندارید");
      return;
    }
    setEditing(true);
    snapshotRef.current = { ...form.getFieldsValue(true) };
  };

  const approve = async () => {
    try {
      await scApproveEvent(row.id);
      message.success("تأیید شد");
      loadEvent(row.id);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };
  const cancelEv = async () => {
    try {
      await scCancelEvent(row.id, "لغو دستی");
      message.success("لغو شد");
      loadEvent(row.id);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };
  const restore = async () => {
    try {
      await scRestoreEvent(row.id);
      message.success("بازگردانده شد");
      loadEvent(row.id);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };
  const remove = async () => {
    try {
      await scDeleteEvent(row.id);
      message.success("حذف شد");
      navigate(ctx?.returnTo || "/supply-chain/events");
    } catch (e) { message.error(e?.response?.data?.detail || "خطا"); }
  };

  const moreItems = useMemo(() => {
    if (!row) return [];
    const itemsMenu = [];
    if (row.status === "cancelled" && can("sc.event.create")) {
      itemsMenu.push({ key: "restore", label: "بازگردانی", icon: <RollbackOutlined />, onClick: restore });
    }
    if (row.status !== "confirmed" && row.status !== "cancelled" && can("sc.event.approve")) {
      itemsMenu.push({ key: "approve", label: "تأیید و ثبت در دفتر", icon: <CheckOutlined />, onClick: approve });
    }
    if (row.status === "confirmed" && can("sc.event.approve")) {
      itemsMenu.push({ key: "cancel", label: "لغو / ابطال", icon: <StopOutlined />, danger: true, onClick: cancelEv });
    }
    if (row.status !== "confirmed" && can("sc.event.create")) {
      itemsMenu.push({ key: "delete", label: "حذف", icon: <DeleteOutlined />, danger: true, onClick: remove });
    }
    itemsMenu.push({
      key: "chat", label: "گفتگو / یادداشت", icon: <CommentOutlined />,
      onClick: () => navigate("/chat?tab=notes"),
    });
    return itemsMenu;
  }, [row, can]); // eslint-disable-line

  const tpl = getTemplate(row?.event_type);
  const wOverride = Form.useWatch("manual_total_override", form);
  const wWeight = Form.useWatch("weight_kg", form);
  const wPrice = Form.useWatch("unit_price", form);
  useEffect(() => {
    if (editing && !wOverride && wWeight && wPrice) {
      form.setFieldsValue({ total_amount: Math.round(Number(wWeight) * Number(wPrice)) });
    }
  }, [wWeight, wPrice, wOverride, editing, form]);

  if (loading) {
    return <div style={{ textAlign: "center", padding: 48 }}><Spin size="large" /></div>;
  }
  if (!row) {
    return (
      <Card>
        <Alert type="error" message="فاکتور یافت نشد" showIcon
          action={<Button onClick={() => navigate("/supply-chain/events")}>بازگشت به فهرست</Button>} />
      </Card>
    );
  }

  const viewTab = (
    <Descriptions bordered size="small" column={1} style={{ direction: "rtl" }}>
      <Descriptions.Item label="کد">{row.event_code}</Descriptions.Item>
      <Descriptions.Item label="نوع عملیات">
        <Tag color={eventColor(row.event_type)}>{eventLabel(row.event_type)}</Tag>
      </Descriptions.Item>
      <Descriptions.Item label="تاریخ">{toJalali(row.event_date)}</Descriptions.Item>
      {row.imported_at && (
        <Descriptions.Item label="تاریخِ ایمپورت">
          {toJalaliDateTime(row.imported_at)}
          {row.import_file && <Text type="secondary" style={{ marginInlineStart: 8, fontSize: 12 }}>({row.import_file})</Text>}
        </Descriptions.Item>
      )}
      <Descriptions.Item label="مبدأ">{row.source_party_name || "—"}</Descriptions.Item>
      <Descriptions.Item label="مقصد">{row.destination_party_name || "—"}</Descriptions.Item>
      <Descriptions.Item label="نوع پارچه/نخ">{row.fabric_type || row.yarn_count || "—"}</Descriptions.Item>
      <Descriptions.Item label="رنگ">{row.color || "—"}</Descriptions.Item>
      <Descriptions.Item label="وزن">{fmtKg(row.weight_kg)}</Descriptions.Item>
      <Descriptions.Item label="کارتن / پالت / طاقه">
        {(row.carton_count ?? "—") + " / " + (row.pallet_count ?? "—") + " / " + (row.roll_count ?? "—")}
      </Descriptions.Item>
      <Descriptions.Item label="شماره مجوز">{row.permit_number || "—"}</Descriptions.Item>
      <Descriptions.Item label="شماره فاکتور">{row.external_invoice_number || "—"}</Descriptions.Item>
      <Descriptions.Item label="شماره سفارش">{row.order_code || "—"}</Descriptions.Item>
      <Descriptions.Item label="شماره سند">{row.external_document_number || "—"}</Descriptions.Item>
      <Descriptions.Item label="فی / جمع">{fmtRial(row.unit_price)} / {fmtRial(row.total_amount)}</Descriptions.Item>
      <Descriptions.Item label="وضعیت"><Tag color={statusMeta(row.status).c}>{statusMeta(row.status).l}</Tag></Descriptions.Item>
      <Descriptions.Item label="یادداشت">{row.notes || "—"}</Descriptions.Item>
    </Descriptions>
  );

  /** تاریخچهٔ تغییرات — «چه کسی، کِی، چه چیزی را عوض کرد».
      برای وقتی که فاکتوری عوض شده و باید معلوم شود کارِ چه کسی بوده. */
  const historyPanel = (
    <Card size="small" style={{ marginTop: 12 }}
      title={<Space size={4}><span>تاریخچهٔ تغییرات</span>
        <HelpPopover label="" title="این بخش چه می‌گوید؟" content={
          <div style={{ fontSize: 13, lineHeight: 2 }}>
            هر تغییری روی این فاکتور اینجا ثبت شده: <b>چه کسی</b>، <b>چه زمانی</b>،
            و <b>کدام فیلد از چه مقداری به چه مقداری</b> رفت.
            <div style={{ marginTop: 6 }}>
              اگر دیدید فاکتوری عوض شده، اول اینجا را نگاه کنید. آپدیتِ برنامه
              هیچ‌وقت دادهٔ فاکتور را تغییر نمی‌دهد؛ هر ردیفی که اینجا می‌بینید
              کارِ یک کاربر بوده — از جمله <b>ویرایشِ دسته‌جمعی</b> که می‌تواند
              چند فاکتور را با هم عوض کند.
            </div>
          </div>
        } /></Space>}
      extra={<Button size="small" onClick={loadHistory} loading={histLoading}>
        {history ? "تازه‌سازی" : "نمایش"}</Button>}>
      {!history ? (
        <Text type="secondary" style={{ fontSize: 12 }}>برای دیدنِ تاریخچه «نمایش» را بزنید.</Text>
      ) : (
        <Table size="small" rowKey="id" pagination={{ pageSize: 10 }}
          dataSource={history.rows || []}
          locale={{ emptyText: "تغییری ثبت نشده" }}
          columns={[
            { title: "زمان", dataIndex: "at", width: 150,
              render: (v) => (v ? `${toJalali(String(v).slice(0, 10))} ${String(v).slice(11, 16)}` : "—") },
            { title: "کاربر", dataIndex: "actor", width: 110 },
            { title: "عملیات", dataIndex: "action_label", width: 90,
              render: (v) => <Tag>{v}</Tag> },
            { title: "تغییرات",
              render: (_, r) => (r.changes?.length
                ? <Space direction="vertical" size={0}>
                    {r.changes.map((c) => (
                      <span key={c.field} style={{ fontSize: 12 }}>
                        <b>{c.label}:</b>{" "}
                        <Text delete type="secondary">{String(c.from ?? "—")}</Text>
                        {" → "}
                        <Text strong>{String(c.to ?? "—")}</Text>
                      </span>
                    ))}
                  </Space>
                : <Text type="secondary" style={{ fontSize: 12 }}>{r.note || "—"}</Text>) },
          ]} />
      )}
    </Card>
  );

  const editForm = (
    <Form form={form} layout="vertical" style={{ direction: "rtl", maxWidth: 640 }}>
      <Form.Item name="event_date" label="تاریخ" rules={[{ required: true }]}>
        <JalaliDateInput />
      </Form.Item>
      {[tpl.sourceSelect, tpl.destSelect].filter(Boolean).map((cfg) => (
        <Form.Item key={cfg.name} name={cfg.name} label={cfg.label}>
          <PartyPicker types={cfg.types} parties={parties} defaultHint={cfg.defaultHint}
            onCreated={(p) => setParties((ps) => [...ps, p])} />
        </Form.Item>
      ))}
      <Form.Item name="weight_kg" label="وزن (کیلوگرم)" rules={[{ required: true }]}>
        <InputNumber min={0.001} style={{ width: "100%" }} size="large" />
      </Form.Item>
      <Form.Item name="item_type_id" label="نوع کالا">
        <Select allowClear size="large" options={items.map((i) => ({ value: i.id, label: i.name }))} />
      </Form.Item>
      <Form.Item name="fabric_type" label="نوع پارچه"><Input size="large" /></Form.Item>
      {/* رنگ از کاتالوگِ مشترک انتخاب می‌شود؛ رنگِ تازه با Enter همان‌جا ثبتِ سراسری می‌شود */}
      <Form.Item name="color" label="رنگ">
        <CreatableSelect options={colorOptions} onCreate={registerColor} size="large" />
      </Form.Item>
      <Collapse items={[{
        key: "docs",
        label: "شماره‌ها و مالی",
        children: (
          <>
            <Form.Item name="external_invoice_number" label="شماره فاکتور"><Input size="large" /></Form.Item>
            <Form.Item name="order_code" label="شماره سفارش"><Input size="large" /></Form.Item>
            <Form.Item name="external_document_number" label="شماره سند"><Input size="large" /></Form.Item>
            <Form.Item name="permit_number" label="شماره مجوز"><Input size="large" /></Form.Item>
            <Form.Item name="roll_count" label="تعداد طاقه"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>
            <Form.Item name="carton_count" label="تعداد کارتن"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>
            <Form.Item name="pallet_count" label="تعداد پالت"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>
            <Form.Item name="unit_price" label="فی (ریال)">
              <InputNumber min={0} style={{ width: "100%" }} size="large"
                formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
            </Form.Item>
            <Form.Item name="manual_total_override" valuePropName="checked">
              <Checkbox>ویرایش دستی مبلغ</Checkbox>
            </Form.Item>
            <Form.Item name="total_amount" label="مبلغ کل">
              <InputNumber min={0} disabled={!wOverride} style={{ width: "100%" }} size="large"
                formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} />
            </Form.Item>
            <Form.Item name="notes" label="توضیحات"><Input.TextArea rows={2} /></Form.Item>
          </>
        ),
      }]} />
    </Form>
  );

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        background: "var(--surface-2,#f6f8fb)", border: "1px solid var(--border,#e4e8f0)",
        borderRadius: 12, padding: "12px 14px", marginBottom: 16,
      }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {row.event_code || `فاکتور #${row.id}`}
          </Title>
          <Space size={8} style={{ marginTop: 4 }}>
            <Tag color={eventColor(row.event_type)}>{eventLabel(row.event_type)}</Tag>
            <Tag color={statusMeta(row.status).c}>{statusMeta(row.status).l}</Tag>
            {ids.length > 0 && (
              <Text type="secondary">فاکتور {displayIdx + 1} از {total}</Text>
            )}
          </Space>
        </div>
        <Space wrap>
          <Button icon={<RightOutlined />} disabled={idx <= 0} onClick={goPrev}>قبلی</Button>
          <Button icon={<LeftOutlined />} disabled={idx < 0 || idx >= ids.length - 1} onClick={goNext}>بعدی</Button>
          {!editing && canEditRow(row, can) && (
            <Button type="primary" icon={<EditOutlined />} onClick={startEdit}>ویرایش</Button>
          )}
          {editing && (
            <>
              <Button icon={<SaveOutlined />} loading={saving} onClick={doSave}>ذخیره</Button>
              <Button type="primary" loading={saving} disabled={idx < 0 || idx >= ids.length - 1}
                onClick={saveAndNext}>ذخیره و بعدی</Button>
            </>
          )}
          {moreItems.length > 0 && (
            <Dropdown menu={{ items: moreItems }} trigger={["click"]}>
              <Button icon={<MoreOutlined />}>بیشتر</Button>
            </Dropdown>
          )}
          <Button icon={<CloseOutlined />} onClick={goClose}>بستن</Button>
        </Space>
      </div>

      <Card>
        <Tabs
          items={[
            {
              key: "details",
              label: "جزئیات",
              children: editing ? editForm : viewTab,
            },
            {
              key: "history",
              label: "تاریخچهٔ تغییرات",
              children: historyPanel,
            },
            {
              key: "chat",
              label: "گفتگو",
              children: (
                <div style={{ padding: 12 }}>
                  <Text type="secondary">یادداشت‌ها و گفتگو در ماژول جداگانه نگه‌داری می‌شود.</Text>
                  <div style={{ marginTop: 12 }}>
                    <Button type="link" icon={<CommentOutlined />} onClick={() => navigate("/chat?tab=notes")}>
                      رفتن به گفتگو / یادداشت
                    </Button>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
